/**
 * Pure model helpers for the per-account notification alert overrides, shared by
 * the settings list (`NotificationSettingsForm`) and the account edit modal
 * (`UpdateAccountModal`), and extracted so the branching stays unit-testable
 * without rendering.
 *
 * An account "override" is its own entry in `financial_accounts.settings`: a
 * non-null threshold for the account's type, or `disableThreshold: true` to mute
 * it. A null/absent value means the account inherits the household default.
 */

import { getThresholdKind, type ThresholdKind } from '../../../utils/accounts';

import { formatDollars } from './notificationSettingsFields';

import type {
  FinancialAccountPrimaryType,
  FinancialAccountSubtype,
  UpdateFinancialAccountSettingsInput,
} from '../../../__generated__/globalTypes';

/** The three per-account settings fields, all nullable (absent = inherit). */
export interface IAccountSettingsValues {
  lowBalanceThreshold?: number | null;
  creditUtilizationThreshold?: number | null;
  disableThreshold?: boolean | null;
}

/** The account shape the settings list consumes (a row of `getFinancialAccountsForUser`). */
export interface IAccountForAlerts {
  id: string;
  name: string;
  type: FinancialAccountPrimaryType;
  subtype: FinancialAccountSubtype;
  mask?: string | null;
  settings?: IAccountSettingsValues | null;
}

/** An eligible account that already has a per-account override, ready to list. */
export interface IConfiguredAccountAlert {
  id: string;
  name: string;
  mask: string | null;
  kind: ThresholdKind;
  threshold: number | null;
  muted: boolean;
}

/** An eligible account with no override yet — an option in the "add" picker. */
export interface IAvailableAccountAlert {
  id: string;
  name: string;
  mask: string | null;
}

/** The account's own threshold value for its supported kind (null = inheriting). */
export const getAccountThreshold = (kind: ThresholdKind, settings: IAccountSettingsValues): number | null =>
  (kind === 'creditUtilization' ? settings.creditUtilizationThreshold : settings.lowBalanceThreshold) ?? null;

/**
 * Whether the account has a per-account override worth listing: a set threshold
 * for its type, or an explicit mute. Everything else is inheriting the
 * household default and belongs in the "add" picker, not the configured list.
 */
export const isAccountConfigured = (kind: ThresholdKind, settings: IAccountSettingsValues): boolean =>
  getAccountThreshold(kind, settings) != null || settings.disableThreshold === true;

/**
 * Builds the settings mutation input, enforcing the backend's non-contradictory
 * invariant: muting sends only `disableThreshold: true` (never a value + mute
 * together — the backend rejects that, and muting alone preserves the stored
 * value server-side). Otherwise it writes the threshold for the account's kind
 * (null clears the override back to the household default) with mute off.
 *
 * The distinction is load-bearing and relies on the mutation being a *partial*
 * update: an omitted threshold field (the mute branch) leaves the stored value
 * untouched, whereas an explicit `null` clears it.
 */
export const buildAccountSettingsInput = (params: {
  id: string;
  kind: ThresholdKind;
  threshold: number | null;
  muted: boolean;
}): UpdateFinancialAccountSettingsInput => {
  const { id, kind, threshold, muted } = params;
  if (muted) {
    return { id, disableThreshold: true };
  }
  return {
    id,
    disableThreshold: false,
    ...(kind === 'creditUtilization' ? { creditUtilizationThreshold: threshold } : { lowBalanceThreshold: threshold }),
  };
};

/**
 * Splits the eligible (depository / credit) accounts into those with a
 * per-account override to list and those still inheriting the household default
 * (the "add" picker's options). Accounts of an unsupported type are dropped.
 */
export const partitionAccountAlerts = (
  accounts: readonly IAccountForAlerts[]
): { configured: IConfiguredAccountAlert[]; available: IAvailableAccountAlert[] } => {
  const configured: IConfiguredAccountAlert[] = [];
  const available: IAvailableAccountAlert[] = [];

  for (const account of accounts) {
    const kind = getThresholdKind(account);
    if (kind === null) {
      continue;
    }
    const settings = account.settings ?? {};
    if (isAccountConfigured(kind, settings)) {
      configured.push({
        id: account.id,
        name: account.name,
        mask: account.mask ?? null,
        kind,
        threshold: getAccountThreshold(kind, settings),
        muted: settings.disableThreshold === true,
      });
    } else {
      available.push({ id: account.id, name: account.name, mask: account.mask ?? null });
    }
  }

  return { configured, available };
};

/** One-line summary of an account's override for the list row (secondary text). */
export const formatAccountAlertSummary = (
  row: Pick<IConfiguredAccountAlert, 'kind' | 'threshold' | 'muted'>
): string => {
  if (row.muted || row.threshold == null) {
    return 'Alerts off';
  }
  return row.kind === 'creditUtilization'
    ? `Alert at ${row.threshold}% utilization`
    : `Alert below $${formatDollars(row.threshold)}`;
};
